import {
    AuthorityFilter,
    OrganisationAuthorityGroup,
    PersonsOrganisationDto,
    SharedAuthorityGroup,
    SimpleAuthorityDto
} from '@flyfreely-portal-ui/flyfreely';
import { toSimpleAuthority } from 'libs/authorities/src/lib/helpers';

export interface MissionAuthorityFilterResult {
    filterOptions: { name: string; children: AuthorityFilter[] }[];
    missionAuthorities: SimpleAuthorityDto[];
}

/**
 * Builds the default "All Missions" authority filter for an organisation,
 * scoped to that organisation only.
 */
export function buildDefaultOrganisationFilter(
    organisation: PersonsOrganisationDto
): AuthorityFilter {
    return {
        name: `All Missions`,
        organisationId: organisation.id,
        authorityIds: null,
        orOrganisationId: null,
        filterType: 'ALL_AUTHORITIES'
    };
}

/**
 * Builds the authority filter option groups from the raw authority data.
 *
 * This is a pure function shared between the mission-view widget and the
 * webview mission list so both components present consistent filtering options.
 *
 * @param organisation The current organisation
 * @param authorities The organisation's own authority groups
 * @param sharedAuthorityGroups Authority groups shared with the organisation
 * @param formatAuthority Formatter function to produce a display label for a
 *   SimpleAuthorityDto (e.g. `a => formatAuthorityPipe.transform(a)`)
 */
export function buildMissionAuthorityFilterOptions(
    organisation: PersonsOrganisationDto,
    authorities: OrganisationAuthorityGroup[],
    sharedAuthorityGroups: SharedAuthorityGroup[],
    formatAuthority: (auth: SimpleAuthorityDto) => string
): MissionAuthorityFilterResult {
    const organisationAuthorities = authorities
        .filter(group => group.hasWorkflow)
        .map(group =>
            group.authorities.map(auth =>
                toSimpleAuthority(auth, organisation, group)
            )
        )
        .reduce((acc, auths) => [...acc, ...auths], []);

    const approvableOrganisationAuthorities = authorities
        .filter(group => group.hasWorkflow)
        .map(group =>
            group.authorities
                .filter(
                    auth =>
                        auth.availableActions.canApproveAuthorityUsage ||
                        auth.availableActions.canApproveAuthorityUsageAsRpic
                )
                .map(auth => toSimpleAuthority(auth, organisation, group))
        )
        .reduce((acc, auths) => [...acc, ...auths], []);

    const sharedAuthorities = sharedAuthorityGroups
        .filter(group => group.hasWorkflow)
        .map(group =>
            group.authorities.map(auth =>
                toSimpleAuthority(auth, auth.organisation, group)
            )
        )
        .reduce((acc, auths) => [...acc, ...auths], []);

    const missionAuthorities: SimpleAuthorityDto[] = [
        ...organisationAuthorities,
        ...sharedAuthorities
    ];

    const defaultFilter = buildDefaultOrganisationFilter(organisation);

    const filterOptions = [
        {
            name: `This organisation only`,
            children: ([defaultFilter] as AuthorityFilter[]).concat(
                missionAuthorities.map(a => ({
                    authorityIds: [a.id],
                    name: `Using ${formatAuthority(a)}`,
                    isPrimary: a.authorityType.isPrimary,
                    organisationId: organisation.id,
                    orOrganisationId: null,
                    filterType: 'SINGLE_AUTHORITY'
                })) as AuthorityFilter[]
            )
        },
        {
            name: `This and shared organisations`,
            children: (
                [
                    {
                        name: `All Missions`,
                        organisationId: null,
                        orOrganisationId: organisation.id,
                        authorityIds: approvableOrganisationAuthorities.map(
                            a => a.id
                        ),
                        filterType: 'ALL_AUTHORITIES'
                    }
                ] as AuthorityFilter[]
            ).concat(
                approvableOrganisationAuthorities.map(a => ({
                    authorityIds: [a.id],
                    isPrimary: a.authorityType.isPrimary,
                    name: `Using ${formatAuthority(a)}`,
                    organisationId: null,
                    orOrganisationId: null,
                    filterType: 'SINGLE_AUTHORITY'
                })) as AuthorityFilter[]
            )
        }
    ];

    return { filterOptions, missionAuthorities };
}
